/**
 * NiftyAISection — Daily Summary section for NiftyTab.
 *
 * Props:
 *   ai             {object}   – AI report (sections, date, created_at, ...)
 *   niftyData      {array}    – daily OHLCV rows; enables PivotBar
 *   livePrice      {number}   – live NIFTY price for PivotBar dot
 *   diagnostics    {object}   – from GET /api/nifty/diagnostics
 *   inputSnapshot  {object}   – frozen structured inputs from _input_snapshot
 *   marketSnapshot {object}   – flat backward-compat snapshot (_market_snapshot)
 */
import React, { useState, useEffect } from 'react';
import { postNiftyCorrection } from '../../api/client';
import RefreshIcon from '../ui/RefreshIcon';

/* ── Pivot levels bar ─────────────────────────────────────────────────────── */
export function PivotBar({ prevRow, livePrice, prevPrevClose }) {
  if (!prevRow?.high || !prevRow?.low || !prevRow?.close) return null;
  const h = prevRow.high, l = prevRow.low, c = prevRow.close;
  const p  = (h + l + c) / 3;
  const r1 = 2 * p - l;
  const r2 = p + (h - l);
  const s1 = 2 * p - h;
  const s2 = p - (h - l);
  const levels = [
    { k:'S2', v:s2, color:'#c62828' },
    { k:'S1', v:s1, color:'#ef5350' },
    { k:'P',  v:p,  color:'var(--ink-3)' },
    { k:'R1', v:r1, color:'#43a047' },
    { k:'R2', v:r2, color:'#2e7d32' },
  ];
  const min = s2, max = r2, range = max - min;
  const pct = v => 4 + Math.max(0, Math.min(92, ((v - min) / range) * 92));
  const cur = livePrice || c;
  const curPct = pct(cur);
  const baseClose = livePrice ? c : prevPrevClose;
  const curColor = !baseClose ? 'var(--ink-3)'
                 : cur > baseClose ? 'var(--up-field-ink)'
                 : cur < baseClose ? 'var(--down-field-ink)'
                 : 'var(--ink-3)';
  const fmt = v => Math.round(v).toLocaleString('en-IN');
  const chgPct = baseClose ? ((cur - baseClose) / baseClose * 100) : null;
  const chgStr = chgPct != null ? ` (${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(2)}%)` : '';

  return (
    <div style={{ marginTop:10, paddingTop:10, borderTop:'.5px solid var(--hair)' }}>
      <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--ink-3)', marginBottom:24 }}>
        Pivot Levels · {prevRow.date}
      </div>
      <div style={{ position:'relative', height:20, overflow:'visible' }}>
        <div style={{ position:'absolute', top:9, left:0, right:0, height:2, background:'var(--hair-strong)', borderRadius:2 }}/>
        {levels.map(lv => (
          <div key={lv.k} style={{ position:'absolute', left:`${pct(lv.v)}%`, top:3, transform:'translateX(-50%)' }}>
            <div style={{ width:1.5, height:14, background:lv.color, opacity:.7 }}/>
          </div>
        ))}
        <div style={{
          position:'absolute', left:`${curPct}%`, top:-16,
          transform:'translateX(-50%)', zIndex:3,
          textAlign:'center', whiteSpace:'nowrap',
          fontSize:11, fontWeight:700, color:curColor, fontFamily:'var(--font-data)',
        }}>
          {livePrice ? 'Live' : 'Close'} · {fmt(cur)}<span style={{ fontWeight:600 }}>{chgStr}</span>
        </div>
        <div style={{
          position:'absolute', left:`${curPct}%`, top:5, transform:'translateX(-50%)', zIndex:2,
          width:10, height:10, borderRadius:'50%', background:curColor, border:'2px solid var(--paper)',
        }}/>
      </div>
      <div style={{ position:'relative', height:30, marginTop:2 }}>
        {levels.map(lv => (
          <div key={lv.k} style={{ position:'absolute', left:`${pct(lv.v)}%`, transform:'translateX(-50%)', textAlign:'center', lineHeight:1.3 }}>
            <div style={{ fontSize:11, fontWeight:700, color:lv.color }}>{lv.k}</div>
            <div style={{ fontSize:11, color:'var(--ink-2)', fontFamily:'var(--font-data)', whiteSpace:'nowrap' }}>{fmt(lv.v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── IST timestamp formatter ─────────────────────────────────────────────── */
export function fmtIst(isoUtc) {
  if (!isoUtc) return '';
  try {
    const d = new Date(isoUtc.replace(' ', 'T') + 'Z');
    return 'Generated ' + d.toLocaleTimeString('en-IN', {
      timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', hour12:false,
    }) + ' IST';
  } catch { return ''; }
}

/* ── Tag → direction ─────────────────────────────────────────────────────── */
const BULL_TOKENS = ['bullish', 'long build', 'low fear', 'risk-on'];
const BEAR_TOKENS = ['bearish', 'short build', 'warning', 'risk-off'];

function tagDirection(label) {
  if (!label) return null;
  const lo = label.toLowerCase();
  if (BULL_TOKENS.some(t => lo.includes(t))) return 'bull';
  if (BEAR_TOKENS.some(t => lo.includes(t))) return 'bear';
  return null;
}

/* ── Tag pill ────────────────────────────────────────────────────────────── */
function TagPill({ label }) {
  if (!label) return null;
  const dir = tagDirection(label);
  const bg    = dir === 'bull' ? 'rgba(30,100,30,0.10)' : dir === 'bear' ? 'rgba(150,40,30,0.10)' : 'rgba(100,80,40,0.10)';
  const color = dir === 'bull' ? 'var(--up-field-ink)'  : dir === 'bear' ? 'var(--down-field-ink)' : 'var(--ink-3)';
  return (
    <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.05em', textTransform:'uppercase', padding:'2px 8px', borderRadius:10, background:bg, color, whiteSpace:'nowrap' }}>
      {label}
    </span>
  );
}

/* ── Factor Agreement pill ───────────────────────────────────────────────── */
function AccuracyPill({ bucketData }) {
  if (!bucketData || bucketData.flag === 'NO_DATA' || bucketData.agreement_pct == null) return null;
  const { flag, agreement_pct, n_days } = bucketData;
  const isBuilding = flag === 'BUILDING';
  const bg    = isBuilding ? 'rgba(120,120,120,0.08)' : flag === 'NEEDS_WORK' ? 'rgba(166,106,0,0.22)' : flag === 'WATCH' ? 'rgba(166,139,77,0.14)' : 'rgba(120,120,120,0.08)';
  const color = isBuilding ? 'var(--ink-3)'           : flag === 'NEEDS_WORK' ? '#6b4e1a'               : flag === 'WATCH' ? '#8a6a35'               : 'var(--ink-3)';
  const label = isBuilding ? `FA ${agreement_pct}% · n=${n_days} (building)` : `FA ${agreement_pct}% · n=${n_days}`;
  return (
    <span style={{ fontSize:9, fontWeight:600, fontFamily:'var(--font-data)', padding:'2px 7px', borderRadius:10, background:bg, color, whiteSpace:'nowrap' }}
      title={`Factor Agreement: ${agreement_pct}% over ${n_days} non-neutral days. ${isBuilding ? 'Sample too small (need n≥20).' : flag === 'NEEDS_WORK' ? 'Below 50% — worse than random.' : flag === 'WATCH' ? '50–57% — marginal.' : '≥58% — reliably predictive.'}`}>
      {label}
    </span>
  );
}

/* ── Correction form ─────────────────────────────────────────────────────── */
const ALL_TAGS = ['BULLISH', 'BEARISH', 'NEUTRAL', 'MIXED', 'LONG BUILD', 'SHORT BUILD', 'SHORT COVERING', 'LONG UNWIND'];

function CorrectionForm({ bucketKey, bucketName, originalTag, reportDate, onClose, onSaved }) {
  const [correctedTag, setCorrectedTag] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [candidateRule, setCandidateRule] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const validTags = bucketKey === 'fut'
    ? ['LONG BUILD', 'SHORT BUILD', 'SHORT COVERING', 'LONG UNWIND', 'NEUTRAL']
    : ['BULLISH', 'BEARISH', 'NEUTRAL', 'MIXED'];

  const handleSave = async () => {
    if (!correctedTag || !reasoning.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await postNiftyCorrection({ bucket_name: bucketName, date_predicted_for: reportDate, original_tag: originalTag, corrected_tag: correctedTag, reasoning: reasoning.trim(), candidate_rule: candidateRule.trim() || null });
      if (res.error) throw new Error(res.error);
      onSaved();
    } catch (e) { setError('Save failed — ' + e.message); setSaving(false); }
  };

  return (
    <div style={{ marginTop:10, padding:'12px 14px', border:'1.5px solid var(--down)', borderRadius:'var(--r-2)', background:'#fff8f6' }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--down-field-ink)', marginBottom:10 }}>Log a correction — {bucketName}</div>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--ink-3)', marginBottom:6 }}>What should this have been?</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
        {validTags.map(tag => (
          <button key={tag} onClick={() => setCorrectedTag(tag)} style={{ fontSize:10, fontWeight:700, letterSpacing:'.04em', textTransform:'uppercase', padding:'3px 10px', borderRadius:10, cursor:'pointer', border: correctedTag === tag ? '1.5px solid var(--amber)' : '.5px solid var(--hair-strong)', background: correctedTag === tag ? 'var(--amber-bg)' : 'var(--surface-2)', color: correctedTag === tag ? 'var(--amber-ink)' : 'var(--ink-2)' }}>{tag}</button>
        ))}
      </div>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--ink-3)', marginBottom:4 }}>Why? (key signal AI missed or mis-weighted)</div>
      <textarea value={reasoning} onChange={e => setReasoning(e.target.value)} rows={3} placeholder="e.g. VIX spike was intraday noise — closing VIX was flat; AI over-weighted the spike" style={{ width:'100%', fontSize:11, padding:'6px 8px', borderRadius:'var(--r-2)', border:'.5px solid var(--hair-strong)', background:'var(--surface)', color:'var(--ink)', resize:'vertical', fontFamily:'var(--font-ui)', boxSizing:'border-box', marginBottom:10 }}/>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--ink-3)', marginBottom:4 }}>Candidate rule to add to lessons file? (optional)</div>
      <textarea value={candidateRule} onChange={e => setCandidateRule(e.target.value)} rows={2} placeholder="e.g. When VIX intraday high > 1.5× opening VIX but closes flat, treat VIX as NEUTRAL" style={{ width:'100%', fontSize:11, padding:'6px 8px', borderRadius:'var(--r-2)', border:'.5px solid var(--hair-strong)', background:'var(--surface)', color:'var(--ink)', resize:'vertical', fontFamily:'var(--font-ui)', boxSizing:'border-box', marginBottom:12 }}/>
      {error && <div style={{ fontSize:11, color:'var(--down-field-ink)', marginBottom:8 }}>{error}</div>}
      <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
        <button onClick={onClose} style={{ fontSize:11, padding:'5px 14px', borderRadius:'var(--r-2)', border:'.5px solid var(--hair-strong)', background:'var(--surface-2)', color:'var(--ink-2)', cursor:'pointer' }}>Cancel</button>
        <button onClick={handleSave} disabled={!correctedTag || !reasoning.trim() || saving} style={{ fontSize:11, fontWeight:600, padding:'5px 14px', borderRadius:'var(--r-2)', border:'none', background:'var(--amber)', color:'var(--paper)', cursor:(!correctedTag||!reasoning.trim()||saving)?'default':'pointer', opacity:(!correctedTag||!reasoning.trim()||saving)?0.5:1 }}>
          {saving ? 'Saving…' : 'Save correction + rule'}
        </button>
      </div>
    </div>
  );
}

/* ── Frozen input detail — shown in expanded card body ───────────────────── */
function FrozenInputDetail({ inputs, eodDate }) {
  if (!inputs?.length) return null;
  const fmtDate = d => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', timeZone:'Asia/Kolkata' }); }
    catch { return d; }
  };

  return (
    <div style={{ marginTop:8, paddingTop:8, borderTop:'.5px solid var(--hair)' }}>
      <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--ink-3)', marginBottom:5 }}>
        Inputs at generation{eodDate ? ` · EOD ${fmtDate(eodDate)}` : ''}
      </div>
      {inputs.map((inp, i) => (
        <div key={i} style={{ marginBottom: inp.multiline ? 6 : 3, display: inp.multiline ? 'block' : 'flex', gap:4, alignItems:'baseline', flexWrap:'wrap' }}>
          <span style={{ fontSize:9.5, fontWeight:700, color:'var(--ink-3)', whiteSpace:'nowrap' }}>{inp.label}</span>
          {inp.status === 'snapshot_error' ? (
            <span style={{ fontSize:10, color:'var(--amber-ink)', fontStyle:'italic', marginLeft:4 }}>snapshot capture failed</span>
          ) : inp.status === 'unavailable' ? (
            <span style={{ fontSize:10, color:'var(--down-field-ink)', fontStyle:'italic', marginLeft:4 }}>unavailable at generation</span>
          ) : inp.status === 'stale' ? (
            <span style={{ fontSize:10, fontFamily:'var(--font-data)', color:'var(--amber-ink)', marginLeft:4 }}>
              {inp.value || '—'} <span style={{ fontSize:9 }}>stale</span>
            </span>
          ) : inp.multiline ? (
            <pre style={{ margin:'3px 0 0 0', padding:'5px 7px', background:'var(--surface-2)', borderRadius:3, fontSize:9.5, fontFamily:'var(--font-data)', color:'var(--ink-2)', whiteSpace:'pre-wrap', wordBreak:'break-word', overflow:'auto', maxHeight:130, lineHeight:1.5 }}>
              {inp.value || '—'}
            </pre>
          ) : (
            <span style={{ fontSize:10.5, fontFamily:'var(--font-data)', color:'var(--ink)', marginLeft:4 }}>{inp.value ?? '—'}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Build per-bucket frozen input sets from inputSnapshot ───────────────── */
function buildFrozenInputs(inputSnapshot) {
  if (!inputSnapshot?.eod) return {};
  const eod  = inputSnapshot.eod;
  const fmtN = (v, d=2) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits:d, maximumFractionDigits:d }) : null;
  const fmtK = v => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr` : null;
  const fmtPct = v => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%` : null;

  return {
    fii: [
      { label:'FII Cash',        value: fmtK(eod.fii?.value),              status: eod.fii?.status },
      { label:'DII Cash',        value: fmtK(eod.dii?.value),              status: eod.dii?.status },
      { label:'FII+PRO EMA',     value: inputSnapshot.fii_ema?.value,      status: inputSnapshot.fii_ema?.status, multiline: true },
    ],
    pcr: [
      { label:'PCR Near',        value: fmtN(eod.pcr_near?.value),         status: eod.pcr_near?.status },
      { label:'PCR Next expiry', value: fmtN(eod.pcr_near_next?.value),    status: eod.pcr_near_next?.status },
    ],
    vix: [
      { label:'VIX',             value: fmtN(eod.vix?.value),              status: eod.vix?.status },
      { label:'VIX change',      value: fmtPct(eod.vix_chg?.value != null ? eod.vix_chg.value : null), status: eod.vix_chg?.status },
    ],
    breadth: [
      { label:'Advances',        value: eod.adv?.value != null ? String(eod.adv.value) : null, status: eod.adv?.status },
      { label:'Declines',        value: eod.dec_count?.value != null ? String(eod.dec_count.value) : null, status: eod.dec_count?.status },
    ],
    fut: [
      { label:'FutOI',           value: eod.fut_oi?.value != null ? fmtN(eod.fut_oi.value, 0) : null, status: eod.fut_oi?.status },
      { label:'OI Δ%',           value: fmtPct(eod.oi_chg_pct?.value),     status: eod.oi_chg_pct?.status },
      { label:'Build-up',        value: eod.build_up?.value,                status: eod.build_up?.status },
    ],
    global: [
      { label:'GIFT Signal',     value: inputSnapshot.gift_signal?.value,   status: inputSnapshot.gift_signal?.status },
      { label:'Global Markets',  value: inputSnapshot.global_markets?.value, status: inputSnapshot.global_markets?.status, multiline: true },
    ],
  };
}

/* ── Bucket card — with frozen input detail in expanded body ─────────────── */
function BucketCard({ bucketKey, label, section, bucketAccuracy, reportDate, promptVersion, expanded, onToggle, frozenInputs, eodDate }) {
  const [showCorrection, setShowCorrection] = useState(false);
  const [savedCorrection, setSavedCorrection] = useState(false);
  const [vote, setVote] = useState(null);

  const tag  = section?.label || '';
  const text = section?.text  || '';
  const handleSaved = () => { setShowCorrection(false); setSavedCorrection(true); };

  return (
    <div style={{ border: expanded ? '1.5px solid var(--amber-hair)' : '.5px solid var(--hair)', borderRadius:'var(--r-2)', background:'var(--surface)', boxShadow: expanded ? '0 2px 8px rgba(0,0,0,0.07)' : 'none', overflow:'hidden', transition:'box-shadow .15s, border-color .15s' }}>
      {/* Collapsed header */}
      <button onClick={onToggle} style={{ display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap:8, width:'100%', padding:'8px 10px', background:'transparent', border:'none', cursor:'pointer', textAlign:'left' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ fontSize:9.5, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--ink-3)', whiteSpace:'nowrap' }}>{label}</span>
            <AccuracyPill bucketData={bucketAccuracy} />
          </div>
        </div>
        <TagPill label={tag} />
        <span style={{ fontSize:10, color:'var(--ink-3)', flexShrink:0 }}>{expanded ? '▾' : '▸'}</span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding:'0 10px 10px', borderTop:'.5px solid var(--hair)' }}>
          <div style={{ fontSize:11, color:'var(--ink)', lineHeight:1.55, paddingTop:8, paddingBottom:10 }}>
            {text || <span style={{ color:'var(--ink-3)', fontStyle:'italic' }}>No analysis available.</span>}
          </div>

          {/* Frozen inputs — the actual values sent to Opus for this factor */}
          {frozenInputs && <FrozenInputDetail inputs={frozenInputs} eodDate={eodDate}/>}

          {/* Footer */}
          {!showCorrection && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
              <span style={{ fontSize:10, color:'var(--ink-3)' }}>
                {reportDate && <span>{reportDate}</span>}
                {promptVersion ? <span style={{marginLeft:4}}>· prompt v{promptVersion}</span> : <span style={{marginLeft:4, opacity:.5}}>· prompt v—</span>}
              </span>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                {savedCorrection ? (
                  <span style={{ fontSize:11, color:'var(--amber-ink)', fontWeight:600 }}>✓ Logged</span>
                ) : (
                  <>
                    <button title="Looks good" onClick={() => setVote(v => v === 'up' ? null : 'up')} style={{ fontSize:13, background:'none', border:'none', cursor:'pointer', padding:'2px 4px', opacity: vote === null ? 0.45 : vote === 'up' ? 1 : 0.2, transition:'opacity .15s' }}>👍</button>
                    <button title="Flag an issue" onClick={() => { setVote('down'); setShowCorrection(true); }} style={{ fontSize:13, background:'none', border:'none', cursor:'pointer', padding:'2px 4px', opacity: vote === null ? 0.45 : vote === 'down' ? 1 : 0.2, transition:'opacity .15s' }}>👎</button>
                  </>
                )}
              </div>
            </div>
          )}

          {showCorrection && (
            <CorrectionForm bucketKey={bucketKey} bucketName={label} originalTag={tag} reportDate={reportDate} onClose={() => { setShowCorrection(false); setVote(null); }} onSaved={handleSaved}/>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Alignment synthesis ─────────────────────────────────────────────────── */
const _BUCKET_KEYS_ALL = ['fii', 'pcr', 'vix', 'breadth', 'fut', 'global'];
const _MIXED_TOKENS    = ['short covering', 'long unwinding', 'long unwind', 'mixed'];

export function computeAlignmentSummary(sections) {
  const counts = { bullish: 0, bearish: 0, neutral: 0, mixed: 0 };
  for (const k of _BUCKET_KEYS_ALL) {
    const dir = tagDirection((sections[k] || {}).label);
    const lo  = ((sections[k] || {}).label || '').toLowerCase();
    if      (dir === 'bull') counts.bullish++;
    else if (dir === 'bear') counts.bearish++;
    else if (_MIXED_TOKENS.some(t => lo.includes(t))) counts.mixed++;
    else counts.neutral++;
  }
  const biasLo  = (sections.bias?.label || '').toLowerCase();
  const dominant = biasLo.includes('bull') ? 'bull' : biasLo.includes('bear') ? 'bear' : null;
  const aligned  = dominant === 'bull' ? counts.bullish : dominant === 'bear' ? counts.bearish : Math.max(counts.bullish, counts.bearish);
  return { counts, dominant, aligned, total: _BUCKET_KEYS_ALL.length };
}

function AlignmentSynthesis({ sections, synthLine }) {
  const { counts } = computeAlignmentSummary(sections);
  const countItems = [
    { label:'bearish', val:counts.bearish, color:'var(--down-field-ink)' },
    { label:'neutral', val:counts.neutral, color:'var(--ink-3)'          },
    { label:'bullish', val:counts.bullish, color:'var(--up-field-ink)'   },
    { label:'mixed',   val:counts.mixed,   color:'var(--amber-ink)'      },
  ].filter(x => x.val > 0);

  return (
    <div style={{ marginTop:8, padding:'8px 12px', border:'1px dashed var(--hair-strong)', borderRadius:'var(--r-2)', background:'var(--surface-2)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
      <div style={{ fontSize:11, color:'var(--ink-2)' }}>
        <span style={{ fontWeight:600, color:'var(--ink-3)', marginRight:4 }}>Alignment:</span>
        {countItems.map((item, i) => (
          <span key={item.label}>
            {i > 0 && <span style={{ color:'var(--ink-3)', margin:'0 4px' }}>·</span>}
            <span style={{ color:item.color, fontWeight:600 }}>{item.val} {item.label}</span>
          </span>
        ))}
      </div>
      {synthLine && <div style={{ fontSize:11, color:'var(--ink-2)', fontStyle:'italic', textAlign:'right', flex:'0 1 auto', maxWidth:260 }}>{synthLine}</div>}
    </div>
  );
}

/* ── Snapshot strip — compact input summary below pivot bar ──────────────── */
function SnapshotStrip({ inputSnapshot, marketSnapshot }) {
  // Prefer structured _input_snapshot (new reports).
  // Fall back to flat _market_snapshot for reports generated before the rebuild.
  // NO live data fallback — if a field is absent, it shows "unavailable", never a live value.
  const snap  = inputSnapshot;
  const old   = marketSnapshot || {};

  const fmtN  = (v, d=2) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits:d, maximumFractionDigits:d }) : null;
  const fmtK  = v => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr` : null;
  const sigC  = v => v == null ? 'var(--ink-2)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
  const sign  = v => v != null && v >= 0 ? '+' : '';

  const fmtShortDate = d => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', timeZone:'Asia/Kolkata' }); }
    catch { return d; }
  };
  const fmtTime = iso => {
    if (!iso) return null;
    try {
      const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
      return d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:false, timeZone:'Asia/Kolkata' }) + ' IST';
    } catch { return null; }
  };

  /* ── Inline row item — shows label + value, or n/a, or err badge ── */
  const Item = ({ label, value, suffix, color, status }) => {
    const isErr      = status === 'snapshot_error';
    const unavailable = !isErr && (status === 'unavailable' || value == null);
    return (
      <span style={{ display:'inline-flex', gap:3, alignItems:'baseline' }}>
        <span style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--ink-3)' }}>{label}</span>
        {isErr ? (
          <span style={{ fontSize:8, color:'var(--amber-ink)', fontStyle:'italic', alignSelf:'center' }}>err</span>
        ) : unavailable ? (
          <span style={{ fontSize:9.5, color:'var(--ink-3)', fontStyle:'italic' }}>—</span>
        ) : (
          <span style={{ fontSize:11, fontWeight:600, fontFamily:'var(--font-data)', color }}>
            {value}{suffix && <span style={{ color:'var(--ink-3)', fontWeight:500 }}>{suffix}</span>}
          </span>
        )}
        {status === 'stale' && !unavailable && !isErr && (
          <span style={{ fontSize:8, color:'var(--amber-ink)', letterSpacing:'.04em', alignSelf:'center' }}>stale</span>
        )}
        {status === 'unavailable' && !isErr && (
          <span style={{ fontSize:8, color:'var(--down-field-ink)', fontStyle:'italic', alignSelf:'center' }}>n/a</span>
        )}
      </span>
    );
  };

  /* ── NEW structured format ── */
  if (snap?.eod) {
    const eod       = snap.eod;
    const gift      = snap.gift_signal || {};
    const eodDate   = snap.eod_date;
    const genAt     = snap.generated_at;

    const close  = eod.close?.value;
    const chgPct = eod.chg_pct?.value;
    const vix    = eod.vix?.value;
    const vixChg = eod.vix_chg?.value;
    const adv    = eod.adv?.value;
    const dec    = eod.dec_count?.value;
    const fii    = eod.fii?.value;
    const dii    = eod.dii?.value;
    const pcr    = eod.pcr_near?.value;

    // Breadth: show "—" (never 0) if either adv or dec unavailable
    const breadthUnavail = eod.adv?.status === 'unavailable' || eod.dec_count?.status === 'unavailable';
    const breadthValue   = (!breadthUnavail && adv != null && dec != null) ? `${adv}▲ / ${dec}▼` : null;
    const breadthColor   = adv > dec ? 'var(--up-field-ink)' : adv < dec ? 'var(--down-field-ink)' : 'var(--ink-2)';

    // GIFT compact: gap_pts extracted at generation time
    const giftGap    = gift.gap_pts;
    const giftStatus = gift.status || 'unavailable';
    const giftValue  = giftGap != null ? `${sign(giftGap)}${Math.abs(giftGap)} pts` : null;
    const giftColor  = sigC(giftGap);

    const eodLabel  = fmtShortDate(eodDate);
    const liveLabel = fmtTime(genAt);

    return (
      <div style={{ marginTop:0 }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px 14px', alignItems:'center' }}>
          <Item label="NIFTY"   value={fmtN(close)}    suffix={chgPct != null ? ` (${sign(chgPct)}${chgPct.toFixed(2)}%)` : null}  color={sigC(chgPct)}  status={eod.close?.status}/>
          <Item label="VIX"     value={fmtN(vix)}      suffix={vixChg != null ? ` (${sign(vixChg)}${vixChg.toFixed(2)})`  : null}  color={vixChg != null ? (vixChg < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)') : 'var(--ink-2)'}  status={eod.vix?.status}/>
          <Item label="Breadth" value={breadthValue}                                                                                   color={breadthColor}  status={breadthUnavail ? 'unavailable' : 'available'}/>
          <Item label="FII"     value={fmtK(fii)}      suffix={dii != null ? ` · DII ${fmtK(dii)}` : null}                          color={sigC(fii)}     status={eod.fii?.status}/>
          <Item label="PCR"     value={fmtN(pcr)}                                                                                     color="var(--ink-2)"  status={eod.pcr_near?.status}/>
          {/* GIFT — always rendered; "unavailable at generation" shown explicitly */}
          <Item label="GIFT"    value={giftValue}                                                                                      color={giftColor}     status={giftStatus}/>
        </div>
        {/* Two-tier freshness stamps */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:5, flexWrap:'wrap', gap:4 }}>
          {eodLabel && <span style={{ fontSize:9, color:'var(--ink-3)' }}>EOD {eodLabel}</span>}
          {liveLabel && <span style={{ fontSize:9, color:'var(--ink-3)' }}>GIFT/Global fetched {liveLabel}</span>}
        </div>
      </div>
    );
  }

  /* ── OLD flat format (backward-compat for pre-rebuild reports) ── */
  if (!Object.keys(old).length) return null;

  const close  = old.close;
  const chgPct = old.chg_pct;
  const vix    = old.vix;
  const vixChg = old.vix_chg;
  const adv    = old.adv;
  const dec    = old.dec_count;
  const fii    = old.fii;
  const dii    = old.dii;
  const pcr    = old.pcr_near;
  const gift   = old.gift_gap_pts;

  return (
    <div style={{ marginTop:10, paddingTop:8, borderTop:'.5px solid var(--hair)' }}>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'5px 14px', alignItems:'center' }}>
        {close != null && <Item label="NIFTY"   value={fmtN(close)}  suffix={chgPct != null ? ` (${sign(chgPct)}${chgPct.toFixed(2)}%)` : null} color={sigC(chgPct)}/>}
        {vix   != null && <Item label="VIX"     value={fmtN(vix)}    suffix={vixChg != null ? ` (${sign(vixChg)}${vixChg.toFixed(2)})`  : null} color={vixChg != null ? (vixChg < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)') : 'var(--ink-2)'}/>}
        {adv   != null && dec != null && <Item label="Breadth" value={`${adv}▲ / ${dec}▼`} color={adv > dec ? 'var(--up-field-ink)' : adv < dec ? 'var(--down-field-ink)' : 'var(--ink-2)'}/>}
        {fii   != null && <Item label="FII"     value={fmtK(fii)}    suffix={dii != null ? ` · DII ${fmtK(dii)}` : null} color={sigC(fii)}/>}
        {pcr   != null && <Item label="PCR"     value={fmtN(pcr)}    color="var(--ink-2)"/>}
        {gift  != null && <Item label="GIFT"    value={`${sign(gift)}${Math.abs(gift)} pts`} color={sigC(gift)}/>}
      </div>
      <div style={{ marginTop:5, fontSize:9, color:'var(--ink-3)' }}>Generation-time data (old format — re-generate for full detail)</div>
    </div>
  );
}

/* ── Bucket definitions ──────────────────────────────────────────────────── */
const BUCKETS = [
  { key:'fii',     label:'FII Trend',         pair:0 },
  { key:'pcr',     label:'PCR Signal',        pair:0 },
  { key:'vix',     label:'VIX Reading',       pair:1 },
  { key:'breadth', label:'Breadth',           pair:1 },
  { key:'fut',     label:'Futures Character', pair:2 },
  { key:'global',  label:'Global Cues',       pair:2 },
];

/* ── Main component ──────────────────────────────────────────────────────── */
export function NiftyAISection({
  ai,
  niftyData,
  livePrice,
  diagnostics,
  inputSnapshot,
  marketSnapshot,
}) {
  const pivotRow      = niftyData?.[niftyData.length - 1];
  const prevPrevClose = niftyData?.[niftyData.length - 2]?.close;
  const sections = ai?.sections || {};

  const biasCls  = sections.bias?.label?.toLowerCase().includes('bull') ? 'bias-bull' : sections.bias?.label?.toLowerCase().includes('bear') ? 'bias-bear' : 'bias-neut';
  const dotCls   = sections.bias?.label?.toLowerCase().includes('bull') ? 'cue-dot-bull' : sections.bias?.label?.toLowerCase().includes('bear') ? 'cue-dot-bear' : 'cue-dot-neut';
  const biasColor = biasCls.includes('bull') ? 'var(--up-field-ink)' : biasCls.includes('bear') ? 'var(--down-field-ink)' : 'var(--amber-ink)';

  const perBucket = diagnostics?.per_bucket || {};
  const synthLine = ai?.alignment_synthesis?.one_liner || null;

  // Per-bucket frozen inputs (only available for new-format reports)
  const frozenByBucket = buildFrozenInputs(inputSnapshot);
  const eodDate        = inputSnapshot?.eod_date;

  // Paired card expansion
  const [expandedPairs, setExpandedPairs] = useState({0:false, 1:false, 2:false});
  const [mobileExpanded, setMobileExpanded] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 720);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isExpanded  = (key, pair) => isMobile ? !!mobileExpanded[key] : !!expandedPairs[pair];
  const handleToggle = (key, pair) => {
    if (isMobile) setMobileExpanded(m => ({...m, [key]: !m[key]}));
    else          setExpandedPairs(p => ({...p, [pair]: !p[pair]}));
  };

  return (
    <div className="card" style={{ padding:12, marginBottom:10 }}>
      {/* Overall Bias */}
      <div className={`bias-row ${biasCls}`} style={{ marginBottom:12, alignItems:'flex-start' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <div className={`cue-dot ${dotCls}`} style={{ width:11, height:11 }}/>
            <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', color:biasColor }}>Overall Bias</span>
            <span className={`cue-pill cp-${dotCls.replace('cue-dot-','')}`} style={{ fontSize:12, padding:'3px 10px', fontWeight:800, letterSpacing:'.06em' }}>
              {sections.bias?.label}
            </span>
          </div>
          <div style={{ fontSize:11.5, fontWeight:400, color:'var(--ink)', lineHeight:1.55 }}>{sections.bias?.text}</div>
          <div style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.07em', color:'var(--ink-3)', marginTop:10, marginBottom:3 }}>Data used for prediction</div>
          <SnapshotStrip inputSnapshot={inputSnapshot} marketSnapshot={marketSnapshot}/>
          {pivotRow && <PivotBar prevRow={pivotRow} livePrice={livePrice} prevPrevClose={prevPrevClose}/>}
        </div>
      </div>

      {/* 6 bucket cards */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:8 }}>
        {BUCKETS.map(({ key, label, pair }) => (
          <BucketCard
            key={key}
            bucketKey={key}
            label={label}
            section={sections[key]}
            bucketAccuracy={perBucket[key]}
            reportDate={ai?.date}
            promptVersion={ai?.prompt_version}
            expanded={isExpanded(key, pair)}
            onToggle={() => handleToggle(key, pair)}
            frozenInputs={frozenByBucket[key]}
            eodDate={eodDate}
          />
        ))}
      </div>
    </div>
  );
}

export default NiftyAISection;
